import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Spinner,
  Alert,
  Badge,
  Label,
  Input,
} from 'reactstrap';
import axiosInstance from '../../api/Axios';

const ProviderManageRoom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchRoom();
  }, [id]);

  const fetchRoom = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/api/rooms/${id}/`);
      setRoom(response.data);
    } catch (err) {
      console.error('Error fetching room:', err);
      setError(err.response?.data?.message || 'Failed to load room details');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestInspection = async () => {
    // Validate that room type is set
    if (!room.room_type) {
      setError('Please set a room type before requesting an inspection. Room type is required for inspection.');
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      setSuccessMessage(null);

      await axiosInstance.patch(`/api/rooms/${id}/`, {
        inspection_status: 'inspection_requested'
      });

      setSuccessMessage('Inspection request submitted successfully! An inspector will be assigned soon.');
      
      // Refresh room data
      await fetchRoom();
    } catch (err) {
      console.error('Error requesting inspection:', err);
      setError(err.response?.data?.message || 'Failed to request inspection');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      setUpdating(true);
      setError(null);
      setSuccessMessage(null);

      const newStatus = room.status === 'available' ? 'inactive' : 'available';

      await axiosInstance.patch(`/api/rooms/${id}/`, {
        status: newStatus
      });

      setSuccessMessage(`Room ${newStatus === 'available' ? 'activated' : 'deactivated'} successfully!`);
      
      // Refresh room data
      await fetchRoom();
    } catch (err) {
      console.error('Error updating room status:', err);
      setError(err.response?.data?.message || 'Failed to update room status');
    } finally {
      setUpdating(false);
    }
  };

  const getInspectionStatusBadge = (status) => {
    const statusMap = {
      'inspection_pending': { color: 'secondary', text: 'Inspection Pending' },
      'inspection_requested': { color: 'info', text: 'Inspection Requested' },
      'inspection_passed': { color: 'success', text: 'Inspection Passed' },
      'inspection_failed': { color: 'danger', text: 'Inspection Failed' },
    };

    const config = statusMap[status] || { color: 'secondary', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const getRoomStatusBadge = (status) => {
    const statusMap = {
      'available': { color: 'success', text: 'Available' },
      'partially_occupied': { color: 'warning', text: 'Partially Occupied' },
      'fully_occupied': { color: 'danger', text: 'Fully Occupied' },
      'maintenance': { color: 'info', text: 'Under Maintenance' },
      'inactive': { color: 'secondary', text: 'Inactive' },
    };

    const config = statusMap[status] || { color: 'secondary', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading room details...</p>
        </div>
      </Container>
    );
  }

  if (error && !room) {
    return (
      <Container className="mt-4">
        <Alert color="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
        <Button color="secondary" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-2"></i>
          Go Back
        </Button>
      </Container>
    );
  }

  const inspectionPassed = room?.inspection_status === 'inspection_passed';

  return (
    <Container className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-door-open me-2"></i>
                Manage Room
              </h2>
              <p className="text-muted mb-0">Room {room?.room_number}</p>
            </div>
            <Button 
              color="secondary" 
              outline 
              onClick={() => navigate(-1)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Go Back
            </Button>
          </div>
        </Col>
      </Row>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert color="success" toggle={() => setSuccessMessage(null)}>
          <i className="bi bi-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Room Information Card */}
      <Row className="mb-4">
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h5" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Room Information
              </CardTitle>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <strong>Room Number:</strong>
                    <p className="mb-0">{room?.room_number}</p>
                  </div>
                  <div className="mb-3">
                    <strong>Floor:</strong>
                    <p className="mb-0">Floor {room?.floor_details?.floor_number}</p>
                  </div>
                  <div className="mb-3">
                    <strong>Block:</strong>
                    <p className="mb-0">Block {room?.floor_details?.block_number}</p>
                  </div>
                  <div className="mb-3">
                    <strong>Number of Beds:</strong>
                    <p className="mb-0">{room?.no_of_beds || 0}</p>
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <strong>Room Status:</strong>
                    <div>{getRoomStatusBadge(room?.status)}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Inspection Status:</strong>
                    <div>{getInspectionStatusBadge(room?.inspection_status)}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Room Gender:</strong>
                    <p className="mb-0">{room?.room_gender ? room.room_gender.charAt(0).toUpperCase() + room.room_gender.slice(1) : 'Mixed'}</p>
                  </div>
                  <div className="mb-3">
                    <strong>Room Type:</strong>
                    <p className="mb-0">{room?.room_type_details?.name || 'Not Assigned'}</p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Inspection Status Section */}
      {inspectionPassed ? (
        /* Inspection Passed - Show Activation Toggle */
        <Row>
          <Col md="12">
            <Card className="border-success">
              <CardBody>
                <CardTitle tag="h5" className="text-success mb-4">
                  <i className="bi bi-check-circle me-2"></i>
                  Room Inspection Passed
                </CardTitle>
                <p className="text-muted">
                  This room has successfully passed inspection. You can now activate or deactivate the room to control its availability for bookings.
                </p>

                {room?.inspection_date && (
                  <div className="mb-3">
                    <small className="text-muted">
                      <i className="bi bi-calendar-check me-2"></i>
                      Inspected on: {new Date(room.inspection_date).toLocaleDateString()}
                    </small>
                  </div>
                )}

                {room?.inspection_notes && (
                  <div className="mb-4 p-3 bg-light rounded">
                    <strong>Inspector's Notes:</strong>
                    <p className="mb-0 mt-2">{room.inspection_notes}</p>
                  </div>
                )}

                <div className="mb-3">
                  <div className="d-flex align-items-center">
                    <div className="form-check form-switch" style={{ fontSize: '1.5rem' }}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="room-status-switch"
                        checked={room?.status === 'available'}
                        onChange={handleToggleStatus}
                        disabled={updating}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                    <Label for="room-status-switch" className="ms-3 mb-0" style={{ cursor: 'pointer' }}>
                      <strong>
                        {room?.status === 'available' ? (
                          <span className="text-success">
                            <i className="bi bi-toggle-on me-2"></i>
                            Room is Available
                          </span>
                        ) : (
                          <span className="text-warning">
                            <i className="bi bi-toggle-off me-2"></i>
                            Room is Inactive
                          </span>
                        )}
                      </strong>
                      <br />
                      <small className="text-muted">
                        {room?.status === 'available'
                          ? 'Room is available for bookings'
                          : 'Room is not available for bookings'}
                      </small>
                    </Label>
                  </div>
                </div>

                <div className="alert alert-info mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  <small>
                    Toggle this switch to make the room {room?.status === 'available' ? 'inactive' : 'available'}.
                    {room?.status === 'available'
                      ? ' Deactivating will make it unavailable for students to book.'
                      : ' Activating will make it available for students to book.'}
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        /* Inspection Not Passed - Show Request Inspection */
        <Row>
          <Col md="12">
            <Card className="border-warning">
              <CardBody>
                <CardTitle tag="h5" className="text-warning mb-4">
                  <i className="bi bi-clipboard-check me-2"></i>
                  Inspection Required
                </CardTitle>
                
                {room?.inspection_status === 'inspection_pending' && (
                  <>
                    <p className="text-muted">
                      This room needs to be inspected before it can be activated. Please request an inspection to proceed.
                    </p>
                    <Button 
                      color="warning" 
                      size="lg"
                      onClick={handleRequestInspection}
                      disabled={updating}
                    >
                      {updating ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Requesting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Request Inspection
                        </>
                      )}
                    </Button>
                  </>
                )}

                {room?.inspection_status === 'inspection_requested' && (
                  <>
                    <Alert color="info">
                      <i className="bi bi-clock-history me-2"></i>
                      <strong>Inspection Requested</strong>
                      <p className="mb-0 mt-2">
                        Your inspection request has been submitted. An inspector will be assigned shortly.
                      </p>
                    </Alert>
                  </>
                )}

                {room?.inspection_status === 'inspection_failed' && (
                  <>
                    <Alert color="danger">
                      <i className="bi bi-x-circle me-2"></i>
                      <strong>Inspection Failed</strong>
                      <p className="mb-0 mt-2">
                        This room did not pass the inspection. Please review the inspector's notes below and address the issues.
                      </p>
                    </Alert>

                    {room?.inspection_notes && (
                      <div className="mb-4 p-3 bg-light rounded">
                        <strong>Inspector's Notes:</strong>
                        <p className="mb-0 mt-2">{room.inspection_notes}</p>
                      </div>
                    )}

                    <Button 
                      color="warning" 
                      onClick={handleRequestInspection}
                      disabled={updating}
                    >
                      {updating ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Requesting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Request Re-Inspection
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ProviderManageRoom;
